import { constVoid, pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { change } from 'redux-form';

import { ServiceTimeClass, ServiceType } from '@mews/enums';
import { DurationService, lt, showSuccessAlert, TimeService, TimeUnitUtils } from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { Service } from '@mews-commander/data-access';

import { RouteName } from '@mews-commander/enums';
import { addServices } from '@mews-commander/legacy-entities/src/actions/services';
import { getTimeOffsetObject } from '@mews-commander/legacy-entities/src/utils/TimeUnitCalculation';
import { getCurrentEnterpriseDefaultLanguageCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { navigateInModal } from '../../app/actions/common';
import { BookableServiceCreateFormValues } from '../types';
import { fieldNames } from '../constants';
import { getDefaulLocalizedTimes } from '../selectors';

export const addBookableService =
    (enterpriseId: string, values: BookableServiceCreateFormValues): CommanderThunkAction =>
    (dispatch, getState, { container }) => {
        const state = getState();
        const languageCode: string = getCurrentEnterpriseDefaultLanguageCode(state, { enterpriseId });

        const { appLocalizationComponent } = container();
        const { bookablePeriod, name, arrivalTime, departureTime, accountingCategoryId, rebateAccountingCategoryId } =
            values;

        const parsedArrivalTime = appLocalizationComponent.parseShortTime(arrivalTime);
        const arrivalTimeHours = TimeService.getHours(parsedArrivalTime);
        const arrivalTimeMinutes = TimeService.getMinutes(parsedArrivalTime);

        const parsedDepartureTime = appLocalizationComponent.parseShortTime(departureTime);
        const departureTimeHours = TimeService.getHours(parsedDepartureTime);
        const departureTimeMinutes = TimeService.getMinutes(parsedDepartureTime);

        const departureTimeObject = getTimeOffsetObject(bookablePeriod, departureTimeHours, departureTimeMinutes);

        const arrivalTimeOffset = DurationService.fromObject({
            hours: arrivalTimeHours,
            minutes: arrivalTimeMinutes,
        });
        const departureTimeOffset = DurationService.fromObject(departureTimeObject);
        const timeUnitPeriod = TimeUnitUtils.serviceClassToTimeUnit(bookablePeriod);

        const serviceData = {
            names: {
                [languageCode]: name,
            },
            data: {
                discriminator: ServiceType.Bookable,
                value: {
                    timeUnitPeriod,
                    endOffset: departureTimeOffset.value,
                    occupancyEndOffset: departureTimeOffset.value,
                    occupancyStartOffset: arrivalTimeOffset.value,
                    startOffset: arrivalTimeOffset.value,
                    accountingCategoryId,
                    rebateAccountingCategoryId,
                },
            },
        };
        return dispatch(addServices({ enterpriseId, services: [serviceData] })).then(
            ({ value: services }: { value: Array<Service> }) =>
                pipe(
                    services,
                    array.head,
                    option.match(constVoid, ({ id }) => {
                        showSuccessAlert({ description: lt('SaveSuccessMessage') });
                        dispatch(navigateToBookableServiceDetailModal(id));
                    })
                )
        );
    };

export const navigateToBookableServiceDetailModal =
    (id: string): CommanderThunkAction =>
    (dispatch, _getState, { resolveRoute }) => {
        const url = resolveRoute(RouteName.VisitDetail, { serviceId: id });
        return dispatch(navigateInModal(url));
    };

export const handleBookablePeriodChange =
    (bookablePeriod: ServiceTimeClass, form: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const { arrivalTime, departureTime } = getDefaulLocalizedTimes(bookablePeriod)(getState());
        dispatch(change(form, fieldNames.ARRIVAL_TIME, arrivalTime));
        dispatch(change(form, fieldNames.DEPARTURE_TIME, departureTime));
    };
